import AuthenticatorAppService from '../../../application/platform/security/authenticator-app-service';
import ApplicationAppService from '../../../application/platform/application-app-service';
import { ACTION_TYPES, useLoginDispatch, useLoginState } from "./login-context";
import { useFeedback } from '../../../hooks/use-feedback';
import { hasErrors } from '../../../utils/validation';
import { validateAuthentication, validateCredentials } from '../login-validation';
import { IPreAuthenticationRequest } from '../../../model/security/request/pre-authentication-request';
import { IAuthenticationRequest } from '../../../model/security/request/authentication-request';
import { IPreAuthenticationResponse } from '../../../model/security/response/pre-authentication-response';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

export const useLoginController = () => {
    const dispatch = useLoginDispatch();
    const state = useLoginState();
    const location = useLocation();
    const navigate = useNavigate();
    const applicationData = ApplicationAppService.getApplicationData();
    const { showSuccess, showWarning, showError } = useFeedback();

    useEffect(() => {
        checkInitialState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function checkInitialState() {
        const queryParams = new URLSearchParams(location.search);
        const action = queryParams.get('action');

        if (action === 'logout') {
            showSuccess('Logout efetuado com sucesso!');
        } else if (action === 'login') {
            showWarning('Para acessar o sistema é necessário efetuar o login.');
        }

        try {
            if (ApplicationAppService.getAuthenticationData()) {
                await AuthenticatorAppService.isAuthenticated();
                navigate(`/${applicationData.initialPage}`);
            }
        } catch (error: any) {
            ApplicationAppService.clearAuthenticationData();
            showError('Não foi possível verificar o estado da sessão', error);
        }
    }

    function handleOnChange(e: any) {
        let type = state.preAuthenticationRequest.type;
        let user = state.preAuthenticationRequest.user;
        let multiTenantId = state.preAuthenticationRequest.multiTenantId;
        let password = state.preAuthenticationRequest.password;
        let companyId = state.authenticationRequest.companyId;
        let remember = state.authenticationRequest.remember;

        if (e.target.name === "select-type") {
            type = e.target.value;

            if (e.target.value === 2 && applicationData !== undefined && applicationData.multitenant !== undefined) {
                companyId = applicationData.multitenant;
            } else if (e.target.value === 1 && applicationData !== undefined && applicationData.multitenant !== undefined) {
                multiTenantId = applicationData.multitenant;
            }
        }
        if (e.target.name === "input-user") {
            user = e.target.value;
        }
        if (e.target.name === "input-password") {
            password = e.target.value;
        }
        if (e.target.name === "select-company") {
            companyId = e.target.value;
        }
        if (e.target.name === "check-remember") {
            remember = e.target.checked;
        }

        dispatch({
            type: ACTION_TYPES.setPreAuthenticationRequest,
            payload: { user, password, type, multiTenantId }
        });

        dispatch({
            type: ACTION_TYPES.setAuthenticationRequest,
            payload: { user, password, type, companyId, remember, module: applicationData.moduleId }
        });
    }

    function toggleShowPassword() {
        dispatch({
            type: ACTION_TYPES.setShowPassword,
            payload: !state.showPassword
        });
    }

    function handleCompanyChange(value: number | string | null) {
        const companyId = value as number;

        dispatch({
            type: ACTION_TYPES.setAuthenticationRequest,
            payload: {
                ...state.authenticationRequest,
                companyId
            }
        });
    }

    const isCompanyStep = (state.preAuthenticationResponse.companies?.length ?? 0) > 0;
    const isCredentialsStep = !isCompanyStep && (applicationData === undefined || state.preAuthenticationRequest.type === 1);
    const validationErrors = isCredentialsStep
        ? validateCredentials(state.preAuthenticationRequest)
        : validateAuthentication(state.authenticationRequest);

    function canSubmit(): boolean {
        const valid = !hasErrors(validationErrors);
        dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: !valid });
        if (!valid) {
            showWarning('É necessário preencher todos os campos obrigatórios.');
        }
        return valid;
    }

    async function preAuthenticate() {
        dispatch({ type: ACTION_TYPES.setLoading, payload: true });
        try {
            const data = await AuthenticatorAppService.preAuthentication(state.preAuthenticationRequest);
            dispatch({ type: ACTION_TYPES.setPreAuthenticationResponse, payload: data });
        } catch (error: any) {
            dispatch({ type: ACTION_TYPES.setPreAuthenticationResponse, payload: {} as IPreAuthenticationResponse });
            showError('Não foi possível validar as credenciais', error);
        } finally {
            dispatch({ type: ACTION_TYPES.setLoading, payload: false });
        }
    }

    async function authenticate() {
        dispatch({ type: ACTION_TYPES.setLoading, payload: true });
        try {
            const data = await AuthenticatorAppService.authentication(state.authenticationRequest);
            ApplicationAppService.setAuthenticationData(data);

            // Garante que foi salvo antes de navegar
            if (!ApplicationAppService.getAuthenticationData()) {
                showError('Erro ao salvar dados de autenticação.');
                return;
            }
            navigate(`/${applicationData.initialPage}`);
        } catch (error: any) {
            dispatch({ type: ACTION_TYPES.setPreAuthenticationResponse, payload: {} as IPreAuthenticationResponse });
            showError('Não foi possível completar a autenticação', error);
        } finally {
            dispatch({ type: ACTION_TYPES.setLoading, payload: false });
        }
    }

    async function authentication() {
        if (!canSubmit()) return;
        if (isCredentialsStep) {
            await preAuthenticate();
            return;
        }
        await authenticate();
    }

    function handleBackButton() {
        dispatch({
            type: ACTION_TYPES.resetForm,
            payload: {
                preAuthenticationRequest: {
                    type: 1,
                    user: "",
                    password: "",
                    multiTenantId: applicationData.multitenant
                } as IPreAuthenticationRequest,
                preAuthenticationResponse: {} as IPreAuthenticationResponse,
                authenticationRequest: {
                    type: 1,
                    user: "",
                    password: "",
                    remember: false,
                    module: applicationData.moduleId
                } as IAuthenticationRequest,
            }
        });
    }

    return {
        handleOnChange,
        toggleShowPassword,
        handleCompanyChange,
        authentication,
        handleBackButton,
        applicationData,
        isCompanyStep,
        validationErrors: state.showValidationErrors ? validationErrors : {}
    };
}
